import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthState } from './hooks/useAuth';
import { useAuthStore } from './store/authStore';

// Layout (eager - needed on every page)
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Pages (lazy-loaded for code splitting)
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const StaffBypass = lazy(() => import('./pages/auth/StaffBypass'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientList = lazy(() => import('./pages/clients/ClientList'));
const ClientDetail = lazy(() => import('./pages/clients/ClientDetail'));
const ClientForm = lazy(() => import('./pages/clients/ClientForm'));
const OrderList = lazy(() => import('./pages/orders/OrderList'));
const OrderForm = lazy(() => import('./pages/orders/OrderForm'));
const OptionList = lazy(() => import('./pages/options/OptionList'));
const OptionForm = lazy(() => import('./pages/options/OptionForm'));
const BreakevenCalculator = lazy(() => import('./pages/calculators/BreakevenCalculator'));
const BreakevenForm = lazy(() => import('./pages/calculators/BreakevenForm'));
const ProfitabilityCalculator = lazy(() => import('./pages/calculators/ProfitabilityCalculator'));
const MarketingPlan = lazy(() => import('./pages/marketing/MarketingPlan'));
const MyMarketingPlan = lazy(() => import('./pages/marketing/MyMarketingPlan'));
const Questionnaires = lazy(() => import('./pages/questionnaires/Questionnaires'));
const DeliveryCalendar = lazy(() => import('./pages/deliveries/DeliveryCalendar'));
const PortfolioHeatmap = lazy(() => import('./pages/dashboard/PortfolioHeatmap'));
const ClientBriefing = lazy(() => import('./pages/clients/ClientBriefing'));
const Reports = lazy(() => import('./pages/reports/Reports'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const MarketingAI = lazy(() => import('./pages/experimental/MarketingAI'));
const InsuranceYieldMatrix = lazy(() => import('./pages/experimental/InsuranceYieldMatrix'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Beef View pages - disabled for production (routes redirect to dashboard)

// Product-aware dashboard (Beef View disabled for production)
function ProductDashboard() {
  const user = useAuthStore((s) => s.user);
  // Customers land on their read-only marketing plan - the staff dashboard
  // has nothing for them.
  if (user?.role === 'client') {
    return <Navigate to="/my-plan" replace />;
  }
  return <Dashboard />;
}

// Loading spinner component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
        <p className="mt-4 text-brand-dark-gray font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthState();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Role-protected route wrapper - redirects unauthorized roles to dashboard
function RoleRoute({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthState();
  const user = useAuthStore((s) => s.user);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Auth Route wrapper - redirect to dashboard if already logged in
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthState();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Maintenance Guard - when VITE_MAINTENANCE_MODE=true, redirect all routes
// to /maintenance unless the session has a staff bypass flag set via /staff-access.
function MaintenanceGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  const hasBypass = typeof window !== 'undefined' && sessionStorage.getItem('staff_bypass') === '1';

  // The one-time reset token is itself the authorization, so a valid reset /
  // forgot-password link must work even while the public site shows the
  // maintenance page (staff set passwords during the maintenance window).
  const allowedPaths = ['/maintenance', '/staff-access', '/reset-password', '/forgot-password'];
  if (allowedPaths.includes(location.pathname)) {
    return <>{children}</>;
  }

  if (maintenanceMode && !hasBypass) {
    return <Navigate to="/maintenance" replace />;
  }

  return <>{children}</>;
}

function App() {
  // Initialize auth state on app mount
  useAuth();

  return (
    <Suspense fallback={<LoadingScreen />}>
      <MaintenanceGuard>
      <Routes>
        {/* Auth routes */}
        <Route
          element={
            <AuthRoute>
              <AuthLayout />
            </AuthRoute>
          }
        >
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* Maintenance page - rendered inside AuthLayout so it shares the
            video-bg + branded card. Reachable directly or via guard redirect. */}
        <Route element={<AuthLayout />}>
          <Route path="/maintenance" element={<Maintenance />} />
          {/* Reset-password lives outside AuthRoute so the one-time token link
              works regardless of auth state (e.g. a staff member already in via
              the bypass) and during the cutover maintenance window. */}
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Staff bypass - hidden URL that sets a session flag and forwards
            to /login. Used during the cutover maintenance window. */}
        <Route path="/staff-access" element={<StaffBypass />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProductDashboard />} />

          {/* Clients - admin/manager only */}
          <Route path="/clients" element={<RoleRoute roles={['admin', 'manager']}><ClientList /></RoleRoute>} />
          <Route path="/clients/new" element={<RoleRoute roles={['admin', 'manager']}><ClientForm /></RoleRoute>} />
          <Route path="/clients/:id" element={<RoleRoute roles={['admin', 'manager']}><ClientDetail /></RoleRoute>} />
          <Route path="/clients/:id/edit" element={<RoleRoute roles={['admin', 'manager']}><ClientForm /></RoleRoute>} />
          <Route path="/clients/:id/briefing" element={<RoleRoute roles={['admin', 'manager']}><ClientBriefing /></RoleRoute>} />

          {/* Orders - admin/manager only */}
          <Route path="/orders" element={<RoleRoute roles={['admin', 'manager']}><OrderList /></RoleRoute>} />
          <Route path="/orders/new" element={<RoleRoute roles={['admin', 'manager']}><OrderForm /></RoleRoute>} />
          <Route path="/orders/:id" element={<RoleRoute roles={['admin', 'manager']}><OrderForm /></RoleRoute>} />

          {/* Options - admin/manager only */}
          <Route path="/options" element={<RoleRoute roles={['admin', 'manager']}><OptionList /></RoleRoute>} />
          <Route path="/options/new" element={<RoleRoute roles={['admin', 'manager']}><OptionForm /></RoleRoute>} />
          <Route path="/options/:id" element={<RoleRoute roles={['admin', 'manager']}><OptionForm /></RoleRoute>} />

          {/* Calculators - admin/manager only */}
          <Route path="/calculators/breakeven" element={<RoleRoute roles={['admin', 'manager']}><BreakevenCalculator /></RoleRoute>} />
          <Route path="/calculators/breakeven/new" element={<RoleRoute roles={['admin', 'manager']}><BreakevenForm /></RoleRoute>} />
          <Route path="/calculators/breakeven/:id/edit" element={<RoleRoute roles={['admin', 'manager']}><BreakevenForm /></RoleRoute>} />
          <Route path="/calculators/breakeven/:id" element={<RoleRoute roles={['admin', 'manager']}><BreakevenCalculator /></RoleRoute>} />
          <Route path="/calculators/profitability" element={<RoleRoute roles={['admin', 'manager']}><ProfitabilityCalculator /></RoleRoute>} />

          {/* Deliveries - admin/manager only */}
          <Route path="/deliveries" element={<RoleRoute roles={['admin', 'manager']}><DeliveryCalendar /></RoleRoute>} />

          {/* Portfolio Heatmap - admin/manager only */}
          <Route path="/portfolio-heatmap" element={<RoleRoute roles={['admin', 'manager']}><PortfolioHeatmap /></RoleRoute>} />

          {/* Marketing - staff view (manager-oriented, hits staff endpoints) */}
          <Route path="/marketing-plan" element={<RoleRoute roles={['admin', 'manager']}><MarketingPlan /></RoleRoute>} />
          <Route path="/marketing-plan/:clientId" element={<RoleRoute roles={['admin', 'manager']}><MarketingPlan /></RoleRoute>} />

          {/* My Marketing Plan - read-only customer view of their own plan */}
          <Route path="/my-plan" element={<MyMarketingPlan />} />

          {/* Questionnaires - staff only (hits staff endpoints) */}
          <Route path="/questionnaires" element={<RoleRoute roles={['admin', 'manager']}><Questionnaires /></RoleRoute>} />

          {/* Reports - admin/manager only */}
          <Route path="/reports" element={<RoleRoute roles={['admin', 'manager']}><Reports /></RoleRoute>} />

          {/* Marketing AI - experimental, admin/manager only */}
          <Route path="/marketing-ai" element={<RoleRoute roles={['admin', 'manager']}><MarketingAI /></RoleRoute>} />

          {/* Insurance & Yield Matrix - experimental, admin/manager only */}
          <Route path="/insurance-yield" element={<RoleRoute roles={['admin', 'manager']}><InsuranceYieldMatrix /></RoleRoute>} />

          {/* Beef View routes - disabled for production, redirect to dashboard */}
          <Route path="/beef/*" element={<Navigate to="/dashboard" replace />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </MaintenanceGuard>
    </Suspense>
  );
}

export default App;
